// eslint configured: rule not present in CRA build
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalIcon } from "lucide-react";
import { apiGet } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { Link } from "react-router-dom";

const SEAL = "var(--seal)";
const INK = "var(--ink)";
const INK_55 = "var(--ink-55)";

function monthLabel(d) { return d.toLocaleDateString(undefined, { month: "long", year: "numeric" }); }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }
function ymdLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Calendar() {
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [data, setData] = useState({ ideas: [], tasks: [] });
  const [selected, setSelected] = useState(null);

  const monthParam = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
  const load = async () => setData(await apiGet("/calendar", { month: monthParam }));
  useEffect(() => { load(); }, [monthParam]);

  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const leadDay = (first.getDay() + 6) % 7;
    const total = daysInMonth(cursor);
    const cells = [];
    for (let i = 0; i < leadDay; i++) cells.push(null);
    for (let i = 1; i <= total; i++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), i));
    while (cells.length % 7) cells.push(null);
    return cells;
  }, [cursor]);

  const byDate = useMemo(() => {
    const m = new Map();
    for (const idea of data.ideas) {
      if (!idea.scheduled_for) continue;
      const d = idea.scheduled_for.slice(0, 10);
      if (!m.has(d)) m.set(d, { ideas: [], tasks: [] });
      m.get(d).ideas.push(idea);
    }
    for (const t of data.tasks) {
      if (!m.has(t.due_date)) m.set(t.due_date, { ideas: [], tasks: [] });
      m.get(t.due_date).tasks.push(t);
    }
    return m;
  }, [data]);

  const move = (delta) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));
  const selectedKey = selected ? ymdLocal(selected) : null;
  const selectedEntry = selectedKey ? byDate.get(selectedKey) : null;
  const todayKey = ymdLocal(new Date());

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="eyebrow"><span className="seal-dot mr-2 align-middle" /> iv. The Calendar</div>
          <h1 className="font-heading mt-4 text-4xl md:text-5xl" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
            A publishing <span className="font-display-italic">month</span>, at rest.
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => move(-1)} className="btn-secondary !p-2" data-testid="cal-prev"><ChevronLeft size={14} /></button>
          <div className="text-sm px-3 font-heading" style={{ fontVariationSettings: "'opsz' 32, 'wght' 450" }} data-testid="cal-label">{monthLabel(cursor)}</div>
          <button onClick={() => move(1)} className="btn-secondary !p-2" data-testid="cal-next"><ChevronRight size={14} /></button>
        </div>
      </div>

      {data.ideas.length === 0 && data.tasks.length === 0 ? (
        <EmptyState
          testid="calendar-empty"
          icon={CalIcon}
          eyebrow="The Calendar"
          title="Nothing scheduled"
          titleItalic="yet."
          body="When you move ideas into Schedule, they appear here. Plan the month, then forget about it."
          primaryLabel="Open the Pipeline"
          onPrimary={() => { window.location.href = "/pipeline"; }}
        />
      ) : (
      <div className="grid grid-cols-7 gap-px rounded-2xl overflow-hidden" style={{ background: "var(--ink-22)", border: "1px solid var(--ink-22)" }} data-testid="cal-grid">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => (
          <div key={d} className="text-[10px] py-2.5 text-center uppercase tracking-[0.2em] font-medium" style={{ background: "var(--bg)", color: INK_55 }}>{d}</div>
        ))}
        {grid.map((d, i) => {
          if (!d) return <div key={i} style={{ background: "var(--bg)", minHeight: 80 }} />;
          const key = ymdLocal(d);
          const entry = byDate.get(key);
          const isToday = key === todayKey;
          const isSelected = selectedKey === key;
          return (
            <button
              key={key}
              onClick={() => setSelected(d)}
              data-testid={`cal-day-${key}`}
              className="min-h-[80px] md:min-h-[100px] p-2 text-left transition relative"
              style={{
                background: isSelected ? "var(--surface)" : "var(--bg)",
                outline: isSelected ? `1px solid ${SEAL}` : "none",
                outlineOffset: "-1px",
              }}
            >
              <div className="text-xs font-medium" style={{ color: isToday ? SEAL : INK_55, fontWeight: isToday ? 700 : 500 }}>{d.getDate()}</div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {(entry?.ideas || []).slice(0, 3).map((idea) => (
                  <span key={idea.id} className="w-1.5 h-1.5 rounded-full" style={{ background: SEAL }} />
                ))}
                {(entry?.tasks || []).slice(0, 3).map((t) => (
                  <span key={t.id} className="w-1.5 h-1.5 rounded-full" style={{ background: t.completed ? "var(--ink-38)" : INK }} />
                ))}
              </div>
            </button>
          );
        })}
      </div>
      )}

      {selected && (
        <div className="card mt-4" data-testid="cal-selected-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="font-heading text-xl" style={{ fontVariationSettings: "'opsz' 48, 'wght' 450" }}>
              {selected.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </div>
            <button className="btn-ghost text-xs" onClick={() => setSelected(null)}>Close</button>
          </div>
          {!selectedEntry || (selectedEntry.ideas.length === 0 && selectedEntry.tasks.length === 0) ? (
            <div className="text-sm" style={{ color: INK_55 }}>Nothing scheduled.</div>
          ) : (
            <div className="space-y-4">
              {selectedEntry.ideas.length > 0 && (
                <div>
                  <div className="eyebrow mb-2">Publishing</div>
                  <ul className="space-y-1.5">
                    {selectedEntry.ideas.map((i) => (
                      <li key={i.id} className="text-sm flex items-center gap-2" style={{ color: INK }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: SEAL }} /> {i.title}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selectedEntry.tasks.length > 0 && (
                <div>
                  <div className="eyebrow mb-2">Tasks</div>
                  <ul className="space-y-1.5">
                    {selectedEntry.tasks.map((t) => (
                      <li key={t.id} className={`text-sm flex items-center gap-2 ${t.completed ? "line-through" : ""}`} style={{ color: t.completed ? INK_55 : INK }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: INK }} /> {t.title}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
